import * as React from 'react';
import isEqual from 'lodash/isEqual';
import {
  PdfViewer,
  PdfViewerRenderer,
  PdfViewerToolbar,
  PdfViewerCloseButton,
  PdfViewerContent,
  PDFViewer,
} from '@cincel.digital/react';
import { useWindowSize } from 'hooks/useWindowSize';
import { Dropzone, PopOver, Spinner } from '@design-system/elements';
import { useMobileDetect } from 'hooks/useIsMobile';
import { translations } from 'translations';
import { useTranslation } from 'react-i18next';
import { useBoolean, useToast } from '@chakra-ui/react';
import { Toolbar } from './components/Toolbar/Toolbar';
import { Button, TrashIcon } from '@design-system/index';
import { DocumentSteps } from '@pages/DocumentPage/CreateDocument';
import { BottomActions } from './components/BottomActions';
import DownloadActions from '../DownloadActions';
import classNames from 'classnames';

type PdfViewport = {
  /**
   * Parent height
   */
  parentHeight?: number;
  /**
   * Detects if file is greater than 20MB
   */
  greaterThan20Mb?: boolean;
  /**
   * Detects if it's creating
   */
  isCreating?: boolean;
  /**
   * Primary color
   */
  primaryColor: string;
  /**
   * Flag that enables/disabled signature field
   */
  isSignatureToolEnabled: boolean;
  /**
   * Signers array
   */
  signers?: Array<any>;
  /**
   * Observer array
   */
  observers?: Array<any>;
  /**
   * Observer array
   */
  observers?: Array<any>;
  /**
   * Current step
   */
  currentStep?: string;
  /**
   * Is button disabled
   */
  isButtonDisabled?: boolean;
  /**
   * Is Loading
   */
  isLoading?: boolean;
  /**
   * Disabled reasons
   */
  disabledReason?: any;
  /**
   * Current file
   */
  document?: File;
  /**
   * Can download
   */
  canDownload?: boolean;
  /**
   * Document name
   */
  name?: string;
  /**
   * Handle step
   */
  handleStep?: (step: string, data?: any) => void;
  /**
   * Handle on file added
   */
  onFileAdded?: (file: File) => void;
  /**
   * Download Nom151
   */
  downloadNom151?: () => void;
  /**
   * Download blockchain certificate
   */
  downloadBlockchain?: () => void;
  /**
   * Download PDF
   */
  downloadPdf?: () => void;
  /**
   * Download fingerprint
   */
  donwloadFingerprint?: () => void;
  /**
   * Is pdf signed
   */
  isSigned?: boolean;

  downloadAll?: () => void;
};

function PdfViewportComponent({
  parentHeight,
  isCreating = false,
  greaterThan20Mb = false,
  primaryColor,
  isSignatureToolEnabled,
  signers = [],
  document,
  onFileAdded,
  currentStep,
  handleStep = () => {},
  isButtonDisabled,
  isLoading,
  disabledReason,
  canDownload = false,
  name,
  downloadNom151 = () => {},
  downloadBlockchain = () => {},
  downloadPdf = () => {},
  donwloadFingerprint = () => {},
  downloadAll,
}: PdfViewport) {
  const pv = React.useRef<any>();

  const { width } = useWindowSize();
  const { isMobile } = useMobileDetect();
  const { t } = useTranslation();
  const toast = useToast();

  const isDesktop = width >= 992;

  const [file, setFile] = React.useState<File | null>(null);
  const [isCalendarOpen, setIsCalenOpen] = React.useState(false);
  const [canDownload20Document, setCanDownload20MbDocument] =
    React.useState(true);
  const [downloadDocumentLoading, setDownloadDocumentLoading] =
    React.useState(false);
  const [isOpen, actions] = useBoolean();
  const [dateToChange, setDateToChange] = React.useState(null);

  const inputFile = React.useRef<HTMLInputElement>(null);
  const calendarOpen = React.useRef(false);

  const handleFile = (evt) => {
    const currFile: File = evt.target.files[0];
    const maxFileSize = 60;

    if (currFile) {
      const fileSize = currFile.size / 1024 / 1024;
      if (fileSize > maxFileSize) {
        toast({
          position: 'top-right',
          isClosable: true,
          title: t(translations.ERRORS.DOCUMENT_SIZE),
          status: 'error',
        });
      } else {
        setFile(currFile);
        if (onFileAdded) onFileAdded(currFile);
      }
    }
  };

  const onDropzoneClick = () => {
    // `current` points to the mounted file input element
    if (inputFile.current) {
      inputFile.current.click();
    }
  };

  const handleFileDrop = React.useCallback(
    (item) => {
      if (item) {
        const currFile = item.files[0];
        if (currFile) {
          setFile(currFile);
          if (onFileAdded) onFileAdded(currFile);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [file, onFileAdded],
  );

  const handleCalendar = (opened) => {
    calendarOpen.current = opened;
    setIsCalenOpen(opened);
  };

  const handleDateSelection = (_date: Date) => {
    handleCalendar(false);
  };

  const handleSubmitDocument = (step, data) => {
    handleStep(step, data);
  };

  const handleNom151 = () => {
    downloadNom151();
  };

  const handleBlockchain = () => {
    downloadBlockchain();
  };

  const handlePdf = () => {
    downloadPdf();
  };

  const downloadMoreThan20MbDocument = () => {
    if (document) {
      setDownloadDocumentLoading(true);
      openDocument(document);
      setTimeout(() => {
        setDownloadDocumentLoading(false);
      }, 10000);
      setCanDownload20MbDocument(false);
    }
  };

  const openDocument = (doc) => {
    try {
      const browser = navigator.userAgent;
      const objectURL = URL.createObjectURL(doc);
      const isChrome = browser.indexOf('Chrome') > -1;
      // Create an anchor element
      const link = window.document.createElement('a');
      if (!isChrome) {
        link.download = `${name} - Cincel`;
      }

      link.href = objectURL;
      link.target = '_blank';

      // Append the link to the DOM
      window.document.body.appendChild(link);

      // Trigger the click event on the anchor element to open the PDF in a new tab
      link.click();

      // Clean up the created element and revoke the Object URL
      link.remove();
      URL.revokeObjectURL(objectURL);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    pv.current = new PDFViewer({
      mode: isCreating ? 'edit' : 'read',
      attributes: {
        annotation_attrs: {
          delete_icon_path: '/icons/trashicon.svg',
        },
      },
    });
    pv.current.debug = process.env.NODE_ENV === 'development';
  }, []);

  if (!pv.current) return null;

  if (!isCreating && !document) return null;

  return (
    <div className="mb-12 flex flex-col md:mb-0 md:h-auto">
      {canDownload && (
        <div className="flex w-full">
          <DownloadActions
            isNom151Enabled
            isBlockchainEnabled
            isBlockchainReady
            isPdfEnabled={canDownload}
            handleNom151={handleNom151}
            handleBlockchain={handleBlockchain}
            handlePdf={handlePdf}
            handleFingerprint={donwloadFingerprint}
          >
            <Button
              //className="mr-auto w-full md:w-auto"
              size="xs"
              variant="outline"
              onClick={() => {
                if (downloadAll) downloadAll();
              }}
              color="#A5A5A5"
            >
              <div className="flex items-center">
                <img
                  src="/img/folder-download-all.svg"
                  alt="Download all"
                  title=""
                  className="h-[20px] w-[20px]"
                />
              </div>
            </Button>
          </DownloadActions>
        </div>
      )}
      {name && (
        <div
          className={classNames(
            'mt-4 text-left text-paragraph font-bold text-gray-500 md:mt-0',
          )}
        >
          {name}
        </div>
      )}
      {greaterThan20Mb && (
        <div className="flex max-h-[500px] min-h-[300px]  w-full flex-col items-center justify-center overflow-y-auto rounded border-2 border-dashed border-gray-100 pt-3">
          <div className="">
            <div className="">
              <img
                src="/img/drag-n-drop.svg"
                alt="drag and drop"
                style={{
                  width: 70,
                  height: 60,
                  marginBottom: 15,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              />
            </div>
            <div className="flex flex-col justify-center">
              <p className="mx-auto w-6/12 text-center">
                {t(translations.DOCUMENT_PAGES.DOCUMENT_MORE_THAN_20MB)}
              </p>
              {status !== 'signed' && (
                <Button
                  className="mx-auto mb-4 mt-2 text-white"
                  color={primaryColor}
                  isDisabled={downloadDocumentLoading || !canDownload20Document}
                  onClick={() => downloadMoreThan20MbDocument()}
                  size="sm"
                  isLoading={downloadDocumentLoading}
                  variant="solid"
                >
                  PDF
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {!greaterThan20Mb && (
        <PdfViewer
          pdfViewer={pv.current}
          file={isCreating ? file : document}
          signers={signers.map((signer: any) => ({
            id: signer.email,
            color: signer.hex,
            name: signer.name,
            ...(signer?.signature_coordinates && !signer.hasSigned
              ? { signatures: signer?.signature_coordinates }
              : {}),
          }))}
          url="https://api.cincel.digital/v3/convert-to-pdf"
          onClose={() => setFile(null)}
          renderEmptyState={(state) => {
            if (state.loading) {
              return (
                <div className="absolute left-0 right-0 z-[1] flex h-full items-center justify-center">
                  <Spinner />
                </div>
              );
            } else if (state.empty) {
              if (isCreating) {
                return (
                  <div
                    className="mb-4 mt-5 md:mt-0 md:py-0"
                    style={{
                      ...(isDesktop
                        ? {
                            height: parentHeight ? parentHeight - 100 : 0,
                          }
                        : {}),
                    }}
                  >
                    <input
                      type="file"
                      id="file"
                      ref={inputFile}
                      accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.pdf"
                      onChange={handleFile}
                      style={{ display: 'none' }}
                    />
                    <Dropzone
                      onClick={onDropzoneClick}
                      label={
                        !isMobile()
                          ? t(translations.COMMON.DRAG_FILE)
                          : t(translations.COMMON.NO_SELECTED_FILES)
                      }
                      handleFileDrop={handleFileDrop}
                      primaryColor={primaryColor}
                      validFiles={t(
                        translations.DOCUMENT_PAGES.CREATE_DOCUMENT_VALID_FILES,
                      )}
                    />
                  </div>
                );
              }
            }
          }}
        >
          <PdfViewerContent
            className="relative flex flex-col-reverse lg:flex-row"
            style={{
              ...(isDesktop && parentHeight
                ? {
                    height: isCreating
                      ? parentHeight - 150
                      : parentHeight - 150,
                    ...(greaterThan20Mb ? { overflow: 'auto' } : {}),
                  }
                : {
                    height: 500,
                  }),
            }}
          >
            <PdfViewerRenderer />
            <div>
              <PdfViewerToolbar
                onBeforeDateChange={(annotationToChange) => {
                  setDateToChange(annotationToChange);
                  actions.on();
                }}
              >
                {({ toolbar, signers: pdfSigners }) => {
                  return (
                    <div>
                      <Toolbar
                        showFullScreen
                        isCreating={isCreating}
                        calendarActions={actions}
                        toolbar={toolbar}
                        isCalendarOpen={isOpen}
                        isSignatureToolEnabled={isSignatureToolEnabled}
                        handleCalendarClickOutside={handleCalendar}
                        signers={pdfSigners}
                        dateToChange={dateToChange}
                        handleCalendarChange={setDateToChange}
                      />
                    </div>
                  );
                }}
              </PdfViewerToolbar>
            </div>
          </PdfViewerContent>
          <div className="mt-3 flex justify-between">
            <BottomActions
              primaryColor={primaryColor}
              currentStep={currentStep}
              disabledReason={disabledReason}
              handleSubmitDocument={handleSubmitDocument}
              isButtonDisabled={isButtonDisabled}
              isLoading={isLoading}
              hasFile={isCreating && Boolean(file)}
            />
          </div>
        </PdfViewer>
      )}
    </div>
  );
}

export const PdfViewport = React.memo(
  PdfViewportComponent,
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  },
);
